import { Link, useNavigate, useLocation } from "react-router-dom";
import { NEXUS_LOGO } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { LogOut, LayoutDashboard, UserCircle, Compass, DollarSign, Megaphone, Trophy, Crown, BookOpen, Wallet as WalletIcon, Sparkles, Menu, Settings as SettingsIcon, Wand2, Play, Users as UsersIcon } from "lucide-react";
import PushBell from "./PushBell";
import { InboxBell } from "./InboxBell";
import { SubscriptionBadge } from "./SubscriptionTierCard";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const linkCls = (path) =>
    `text-sm tracking-wide transition-colors ${
      location.pathname === path ? "text-white" : "text-zinc-400 hover:text-white"
    }`;

  return (
    <header
      className="sticky top-0 z-40 glass border-b border-white/5 pt-[env(safe-area-inset-top)]"
      data-testid="nexus-navbar"
    >
      <div className="max-w-[1400px] mx-auto flex items-center gap-4 md:gap-8 px-4 md:px-6 lg:px-10 h-16">
        <Link
          to="/"
          className="flex items-center gap-2.5 shrink-0 mr-auto xl:mr-0"
          data-testid="nav-logo-link"
        >
          <img
            src={NEXUS_LOGO}
            alt="Nexus"
            className="h-8 w-8 object-contain"
          />
          <span className="font-display font-bold text-lg tracking-tight">NEXUS</span>
        </Link>

        <nav className="hidden xl:flex items-center gap-5 xl:gap-7 mr-auto pr-6">
          <Link
            to={user?.role === "creator" ? "/campaigns" : "/discover"}
            className={`${linkCls(user?.role === "creator" ? "/campaigns" : "/discover")} whitespace-nowrap`}
            data-testid="nav-discover"
          >
            {user?.role === "creator" ? "Brand Collabs" : "Discover"}
          </Link>
          <Link to="/explore" className={`${linkCls("/explore")} whitespace-nowrap`} data-testid="nav-explore">
            Explore
          </Link>
          {!user && (
            <Link to="/how-it-works" className={`${linkCls("/how-it-works")} whitespace-nowrap`} data-testid="nav-how-it-works">
              How it works
            </Link>
          )}
          {(!user || user.role === "creator") && (
            <Link to="/growth" className={`${linkCls("/growth")} inline-flex items-center gap-1 whitespace-nowrap`} data-testid="nav-growth">
              <Sparkles className="h-3.5 w-3.5 text-nexus-pink" />
              Growth AI
            </Link>
          )}
          {user?.role === "creator" && (
            <>
              <Link to="/creator-studio" className={`${linkCls("/creator-studio")} inline-flex items-center gap-1 whitespace-nowrap`} data-testid="nav-creator-studio">
                <Wand2 className="h-3.5 w-3.5 text-nexus-pink" />
                Studio
              </Link>
              <Link to="/negotiations" className={`${linkCls("/negotiations")} whitespace-nowrap`} data-testid="nav-negotiations">
                Negotiations
              </Link>
              <Link to="/collabs" className={`${linkCls("/collabs")} whitespace-nowrap`} data-testid="nav-collabs">
                Collabs
              </Link>
              <Link to="/messages" className={`${linkCls("/messages")} whitespace-nowrap`} data-testid="nav-messages">
                Messages
              </Link>
            </>
          )}
          {user?.role !== "creator" && (
            <Link to="/campaigns" className={`${linkCls("/campaigns")} whitespace-nowrap`} data-testid="nav-campaigns">
              Campaigns
            </Link>
          )}
          <Link to="/leaderboard" className={`${linkCls("/leaderboard")} whitespace-nowrap`} data-testid="nav-leaderboard">
            Leaderboard
          </Link>
          <Link to="/hall-of-fame" className={`${linkCls("/hall-of-fame")} whitespace-nowrap`} data-testid="nav-hall-of-fame">
            Hall of Fame
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="text-sm tracking-wide text-zinc-400 hover:text-white transition-colors whitespace-nowrap inline-flex items-center gap-1"
                data-testid="nav-more"
              >
                More
                <span className="text-xs">▾</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-[#0C0C10] border-white/10 text-white w-52"
            >
              {user?.role === "creator" && (
                <>
                  <DropdownMenuItem
                    onClick={() => navigate("/inspiration")}
                    data-testid="nav-inspiration"
                    className="focus:bg-white/5 focus:text-white cursor-pointer"
                  >
                    <Sparkles className="h-4 w-4 mr-2 text-nexus-pink" /> Inspiration Hub
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate("/academy")}
                    data-testid="nav-academy"
                    className="focus:bg-white/5 focus:text-white cursor-pointer"
                  >
                    <BookOpen className="h-4 w-4 mr-2" /> Academy
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuItem
                onClick={() => navigate("/blog")}
                data-testid="nav-blog"
                className="focus:bg-white/5 focus:text-white cursor-pointer"
              >
                <BookOpen className="h-4 w-4 mr-2" /> Blog
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate("/case-studies")}
                data-testid="nav-case-studies"
                className="focus:bg-white/5 focus:text-white cursor-pointer"
              >
                <Trophy className="h-4 w-4 mr-2" /> Case Studies
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate("/pricing")}
                data-testid="nav-pricing"
                className="focus:bg-white/5 focus:text-white cursor-pointer"
              >
                <DollarSign className="h-4 w-4 mr-2" /> Pricing
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate("/contact")}
                data-testid="nav-contact"
                className="focus:bg-white/5 focus:text-white cursor-pointer"
              >
                <UserCircle className="h-4 w-4 mr-2" /> Contact & support
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {user && (
            <Link
              to={user.role === "brand" ? "/brand" : "/creator"}
              className={`${linkCls(user.role === "brand" ? "/brand" : "/creator")} whitespace-nowrap`}
              data-testid="nav-dashboard"
            >
              Dashboard
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          {/* Compact menu for < lg widths */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="xl:hidden h-9 w-9 rounded-full glass hairline flex items-center justify-center hover:border-white/20 transition-colors"
                data-testid="nav-mobile-menu-trigger"
                aria-label="Open menu"
              >
                <Menu className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#0C0C10] border-white/10 text-white w-56">
              <DropdownMenuItem onClick={() => navigate(user?.role === "creator" ? "/campaigns" : "/discover")} className="focus:bg-white/5 focus:text-white cursor-pointer">
                <Compass className="h-4 w-4 mr-2" />{user?.role === "creator" ? "Brand Collabs" : "Discover"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/explore")} className="focus:bg-white/5 focus:text-white cursor-pointer" data-testid="nav-mobile-explore">
                <Play className="h-4 w-4 mr-2" />Explore
              </DropdownMenuItem>
              {(!user || user.role === "creator") && (
                <DropdownMenuItem onClick={() => navigate("/growth")} className="focus:bg-white/5 focus:text-white cursor-pointer"><Sparkles className="h-4 w-4 mr-2 text-nexus-pink" />Growth AI</DropdownMenuItem>
              )}
              {user?.role === "creator" && (
                <>
                  <DropdownMenuItem onClick={() => navigate("/creator-studio")} className="focus:bg-white/5 focus:text-white cursor-pointer"><Wand2 className="h-4 w-4 mr-2 text-nexus-pink" />Creator Studio</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/negotiations")} className="focus:bg-white/5 focus:text-white cursor-pointer"><Megaphone className="h-4 w-4 mr-2" />Negotiations</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/collabs")} className="focus:bg-white/5 focus:text-white cursor-pointer"><Sparkles className="h-4 w-4 mr-2" />Collabs</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/messages")} className="focus:bg-white/5 focus:text-white cursor-pointer"><Megaphone className="h-4 w-4 mr-2" />Messages</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/inspiration")} className="focus:bg-white/5 focus:text-white cursor-pointer"><Sparkles className="h-4 w-4 mr-2 text-nexus-pink" />Inspiration Hub</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/academy")} className="focus:bg-white/5 focus:text-white cursor-pointer"><BookOpen className="h-4 w-4 mr-2" />Academy</DropdownMenuItem>
                </>
              )}
              {user?.role !== "creator" && (
                <DropdownMenuItem onClick={() => navigate("/campaigns")} className="focus:bg-white/5 focus:text-white cursor-pointer"><Megaphone className="h-4 w-4 mr-2" />Campaigns</DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => navigate("/leaderboard")} className="focus:bg-white/5 focus:text-white cursor-pointer"><Trophy className="h-4 w-4 mr-2" />Leaderboard</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/hall-of-fame")} className="focus:bg-white/5 focus:text-white cursor-pointer"><Crown className="h-4 w-4 mr-2" />Hall of Fame</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/blog")} className="focus:bg-white/5 focus:text-white cursor-pointer"><BookOpen className="h-4 w-4 mr-2" />Blog</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/case-studies")} className="focus:bg-white/5 focus:text-white cursor-pointer"><Trophy className="h-4 w-4 mr-2" />Case Studies</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/pricing")} className="focus:bg-white/5 focus:text-white cursor-pointer"><DollarSign className="h-4 w-4 mr-2" />Pricing</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/contact")} className="focus:bg-white/5 focus:text-white cursor-pointer"><UserCircle className="h-4 w-4 mr-2" />Contact & support</DropdownMenuItem>
              {user && (
                <DropdownMenuItem onClick={() => navigate(user.role === "brand" ? "/brand" : "/creator")} className="focus:bg-white/5 focus:text-white cursor-pointer"><LayoutDashboard className="h-4 w-4 mr-2" />Dashboard</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {user && <InboxBell />}
          {user && <PushBell compact />}
          {user && <SubscriptionBadge onClick={() => navigate("/pricing")} />}
          {!user ? (
            <>
              <Button
                variant="ghost"
                className="text-white hover:bg-white/5 hover:text-white"
                onClick={() => navigate("/auth?mode=login")}
                data-testid="nav-login-btn"
              >
                Log in
              </Button>
              <Button
                className="nexus-gradient text-white font-semibold rounded-full px-5 hover:opacity-90 border-0"
                onClick={() => navigate("/auth?mode=signup")}
                data-testid="nav-signup-btn"
              >
                Get started
              </Button>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 px-3 py-2 rounded-full glass hairline hover:border-white/20 transition-colors"
                  data-testid="nav-user-menu-trigger"
                >
                  <div className="h-7 w-7 rounded-full nexus-gradient flex items-center justify-center text-xs font-bold">
                    {user.name?.[0]?.toUpperCase()}
                  </div>
                  <span className="text-sm hidden sm:block max-w-[110px] truncate">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-[#0C0C10] border-white/10 text-white w-56"
              >
                <DropdownMenuLabel className="text-zinc-400 text-xs uppercase tracking-widest">
                  {user.role}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  onClick={() => navigate(user.role === "brand" ? "/brand" : "/creator")}
                  data-testid="menu-dashboard"
                  className="focus:bg-white/5 focus:text-white cursor-pointer"
                >
                  <LayoutDashboard className="h-4 w-4 mr-2" />
                  Dashboard
                </DropdownMenuItem>
                {user.role === "creator" && (
                  <DropdownMenuItem
                    onClick={() => navigate("/growth")}
                    data-testid="menu-growth"
                    className="focus:bg-white/5 focus:text-white cursor-pointer"
                  >
                    <Sparkles className="h-4 w-4 mr-2 text-nexus-pink" />
                    Growth AI
                  </DropdownMenuItem>
                )}
                {user.role === "creator" && (
                  <DropdownMenuItem
                    onClick={() => navigate("/creator/profile")}
                    data-testid="menu-profile"
                    className="focus:bg-white/5 focus:text-white cursor-pointer"
                  >
                    <UserCircle className="h-4 w-4 mr-2" />
                    Edit Profile
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => navigate("/discover")}
                  className="focus:bg-white/5 focus:text-white cursor-pointer"
                >
                  <Compass className="h-4 w-4 mr-2" />
                  Discover
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/campaigns")}
                  className="focus:bg-white/5 focus:text-white cursor-pointer"
                >
                  <Megaphone className="h-4 w-4 mr-2" />
                  Campaigns
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/how-it-works")}
                  className="focus:bg-white/5 focus:text-white cursor-pointer"
                  data-testid="menu-how-it-works"
                >
                  <Compass className="h-4 w-4 mr-2" />
                  How it works
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/leaderboard")}
                  className="focus:bg-white/5 focus:text-white cursor-pointer"
                >
                  <Trophy className="h-4 w-4 mr-2" />
                  Leaderboard
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/hall-of-fame")}
                  className="focus:bg-white/5 focus:text-white cursor-pointer"
                >
                  <Crown className="h-4 w-4 mr-2" />
                  Hall of Fame
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/blog")}
                  className="focus:bg-white/5 focus:text-white cursor-pointer"
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  Blog
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/groups")} data-testid="menu-groups" className="focus:bg-white/5 focus:text-white cursor-pointer">
                  <UsersIcon className="h-4 w-4 mr-2" />
                  Group chats
                </DropdownMenuItem>
                {user?.role === "creator" && (
                  <DropdownMenuItem onClick={() => navigate("/affiliates")} data-testid="menu-affiliates" className="focus:bg-white/5 focus:text-white cursor-pointer">
                    <Compass className="h-4 w-4 mr-2" />
                    Ads & affiliates
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => navigate("/wallet")}
                  data-testid="menu-wallet"
                  className="focus:bg-white/5 focus:text-white cursor-pointer"
                >
                  <WalletIcon className="h-4 w-4 mr-2" />
                  Wallet
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/pricing")}
                  className="focus:bg-white/5 focus:text-white cursor-pointer"
                >
                  <DollarSign className="h-4 w-4 mr-2" />
                  Pricing
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  onClick={() => navigate("/settings")}
                  data-testid="menu-settings"
                  className="focus:bg-white/5 focus:text-white cursor-pointer"
                >
                  <SettingsIcon className="h-4 w-4 mr-2" />
                  Account Settings
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => { navigate("/", { replace: true }); logout(); }}
                  data-testid="menu-logout"
                  className="focus:bg-white/5 focus:text-white cursor-pointer text-nexus-pink"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}
